import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Upload as UploadIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { createSuppliersBatch } from '../services/api';

const BatchSupplierUpload = ({ open, onClose, onSuccess }) => {
  const [csvData, setCsvData] = useState(null);
  const [parsedSuppliers, setParsedSuppliers] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [errors, setErrors] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setErrors(['Please select a valid CSV file']);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const csvContent = e.target.result;
      parseCSV(csvContent);
    };
    reader.readAsText(file);
  };

  const parseCSV = (csvContent) => {
    const lines = csvContent.split('\n');
    const headers = lines[0].split(',').map(h => h.trim());
    
    const suppliers = [];
    const newErrors = [];
    
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      const values = line.split(',').map(v => v.trim());
      const row = {};
      
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      
      // Validate required fields
      const name = row['Name'] || row['name'] || '';
      const email = row['Email'] || row['email'] || '';
      
      if (!name) {
        newErrors.push(`Line ${i + 1}: Name is required`);
        continue;
      }
      
      if (!email) {
        newErrors.push(`Line ${i + 1}: Email is required`);
        continue;
      }
      
      if (!email.includes('@')) {
        newErrors.push(`Line ${i + 1}: Invalid email format`);
        continue;
      }
      
      // Parse optional fields
      let initialBalance = 0;
      try {
        initialBalance = parseFloat(row['Initial Balance'] || row['initial_balance'] || '0');
      } catch (e) {
        newErrors.push(`Line ${i + 1}: Invalid Initial Balance format`);
        continue;
      }
      
      suppliers.push({
        name: name,
        email: email.toLowerCase(),
        balance: initialBalance,
        street: row['street'] || row['Street'] || '',
        city: row['City'] || row['city'] || '',
        state: row['State'] || row['state'] || '',
        country: row['Country'] || row['country'] || '',
        iban: row['IBAN'] || row['iban'] || '',
        swift_code: row['SWIFT Code'] || row['swift_code'] || row['SWIFT'] || ''
      });
    }
    
    setParsedSuppliers(suppliers);
    setSelectedSuppliers(suppliers.map((_, index) => index)); // Select all by default
    setErrors(newErrors);
    setCsvData(csvContent);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedSuppliers(parsedSuppliers.map((_, index) => index));
    } else {
      setSelectedSuppliers([]);
    }
  };

  const handleSelectSupplier = (index) => {
    const newSelected = selectedSuppliers.includes(index)
      ? selectedSuppliers.filter(i => i !== index)
      : [...selectedSuppliers, index];
    setSelectedSuppliers(newSelected);
  };

  const handleUpload = async () => {
    if (selectedSuppliers.length === 0) {
      setErrors(['Please select at least one supplier to upload']);
      return;
    }

    setIsUploading(true);
    setErrors([]);

    try {
      // Create a new CSV with only selected suppliers
      const selectedSuppliersData = selectedSuppliers.map(index => parsedSuppliers[index]);
      
      // Create CSV content
      const headers = ['Name', 'Email', 'Initial Balance', 'street', 'City', 'State', 'Country', 'IBAN', 'SWIFT Code'];
      const csvContent = [
        headers.join(','),
        ...selectedSuppliersData.map(supplier => [
          supplier.name,
          supplier.email,
          supplier.balance,
          supplier.street,
          supplier.city,
          supplier.state,
          supplier.country,
          supplier.iban,
          supplier.swift_code
        ].join(','))
      ].join('\n');

      // Create FormData with the CSV
      const formData = new FormData();
      const csvBlob = new Blob([csvContent], { type: 'text/csv' });
      formData.append('file', csvBlob, 'suppliers.csv');

      const response = await createSuppliersBatch(formData);
      setUploadResult(response.data);
      
      if (response.data.suppliers_created > 0) {
        onSuccess();
      }
    } catch (error) {
      console.error('Upload error:', error);
      setErrors([error.response?.data?.detail || error.message || 'Upload failed']);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setCsvData(null);
    setParsedSuppliers([]);
    setSelectedSuppliers([]);
    setErrors([]);
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const downloadTemplate = () => {
    const template = `Name,Email,Initial Balance,street,City,State,Country,IBAN,SWIFT Code
Example Supplier,example@email.com,1000.00,123 Main St,New York,NY,United States,GB29NWBK60161331926819,NWBKGB2L`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'suppliers_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)'
        }
      }}
    >
      <DialogTitle sx={{ 
        fontSize: '1.5rem', 
        fontWeight: 600,
        borderBottom: '1px solid #f0f0f0',
        pb: 2
      }}>
        Batch Upload Suppliers
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        {!csvData ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" gutterBottom>
              Upload CSV File
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Upload a CSV file with supplier information. Required fields: Name, Email. Optional fields: Initial Balance, Street, City, State, Country, IBAN, SWIFT Code.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
              <input
                accept=".csv"
                type="file"
                onChange={handleFileSelect}
                ref={fileInputRef}
                style={{ display: 'none' }}
              />
              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ borderRadius: '8px', textTransform: 'none' }}
              >
                Choose CSV File
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={downloadTemplate}
                sx={{ borderRadius: '8px', textTransform: 'none' }}
              >
                Download Template
              </Button>
            </Box>
            
            {errors.length > 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  {errors.map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </Typography>
              </Alert>
            )}
          </Box>
        ) : uploadResult ? (
          <Box>
            <Alert 
              severity={uploadResult.errors && uploadResult.errors.length > 0 ? "warning" : "success"}
              sx={{ mb: 3 }}
            >
              <Typography variant="h6">
                {uploadResult.message}
              </Typography>
              <Typography variant="body2">
                Suppliers created: {uploadResult.suppliers_created}
              </Typography>
            </Alert>
            
            {uploadResult.errors && uploadResult.errors.length > 0 && (
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'rgba(211, 47, 47, 0.05)' }}>
                <Typography variant="subtitle1" color="error" gutterBottom>
                  Errors:
                </Typography>
                {uploadResult.errors.map((error, index) => (
                  <Typography key={index} variant="body2" color="error">
                    {error}
                  </Typography>
                ))}
              </Paper>
            )}
            
            {uploadResult.created_suppliers && uploadResult.created_suppliers.length > 0 && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Successfully Created Suppliers:
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Balance</TableCell>
                        <TableCell>Location</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {uploadResult.created_suppliers.map((supplier, index) => (
                        <TableRow key={index}>
                          <TableCell>{supplier.name}</TableCell>
                          <TableCell>{supplier.email}</TableCell>
                          <TableCell>${supplier.balance}</TableCell>
                          <TableCell>
                            {supplier.city && supplier.country 
                              ? `${supplier.city}, ${supplier.country}`
                              : 'Not provided'
                            }
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Preview Suppliers ({parsedSuppliers.length} found)
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedSuppliers.length === parsedSuppliers.length}
                    indeterminate={selectedSuppliers.length > 0 && selectedSuppliers.length < parsedSuppliers.length}
                    onChange={handleSelectAll}
                  />
                }
                label="Select All"
              />
            </Box>
            
            {errors.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  {errors.map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </Typography>
              </Alert>
            )}
            
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox"></TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Balance</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Banking</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsedSuppliers.map((supplier, index) => (
                    <TableRow key={index}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedSuppliers.includes(index)}
                          onChange={() => handleSelectSupplier(index)}
                        />
                      </TableCell>
                      <TableCell>{supplier.name}</TableCell>
                      <TableCell>{supplier.email}</TableCell>
                      <TableCell>${supplier.balance}</TableCell>
                      <TableCell>
                        {supplier.city && supplier.country 
                          ? `${supplier.city}, ${supplier.country}`
                          : 'Not provided'
                        }
                      </TableCell>
                      <TableCell>
                        {supplier.iban ? (
                          <Box>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                              IBAN: {supplier.iban}
                            </Typography>
                            {supplier.swift_code && (
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                SWIFT: {supplier.swift_code}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          'Not provided'
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          icon={<CheckIcon />}
                          label="Valid" 
                          color="success" 
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {selectedSuppliers.length} of {parsedSuppliers.length} suppliers selected
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setCsvData(null);
                    setParsedSuppliers([]);
                    setSelectedSuppliers([]);
                    setErrors([]);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  sx={{ borderRadius: '8px', textTransform: 'none' }}
                >
                  Upload Different File
                </Button>
                
                <Button
                  variant="contained"
                  onClick={handleUpload}
                  disabled={isUploading || selectedSuppliers.length === 0}
                  startIcon={isUploading ? <CircularProgress size={20} /> : <UploadIcon />}
                  sx={{ borderRadius: '8px', textTransform: 'none' }}
                >
                  {isUploading ? 'Uploading...' : `Upload ${selectedSuppliers.length} Suppliers`}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f0f0f0' }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          sx={{ borderRadius: '8px', textTransform: 'none' }}
        >
          {uploadResult ? 'Close' : 'Cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BatchSupplierUpload; 